import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { withToastManager } from "react-toast-notifications";
import BrandLinksRow from "../../Helper/BrandLinksRow";
import Helper from "../../Helper/helper";
import { fetchProjectBySlug } from "../../Helper/projects";
import ToastDemo from "../../Helper/toaster";
import EmptyState from "../../Dashboard/EmptyState";
import ExperienceGate from "../../Experience/ExperienceGate";
import OrganizationActionBar from "../../Organization/Public/OrganizationActionBar";
import RichTextContent from "../../Helper/RichTextContent";
import EntityIdentity from "../../Helper/EntityIdentity";
import ShareMenu from "../../Helper/ShareMenu";
import { AppearanceButton } from "../../Appearance";
import {
  resolveMediaUrl,
  resolveMediaBackground,
  handleMediaImgError,
} from "../../Helper/mediaUrl";
import ProjectFundingPanel from "./ProjectFundingPanel";
import ProjectMilestones from "./ProjectMilestones";
import ProjectUpdates from "./ProjectUpdates";
import ActivityFeedSection from "../../Community/ActivityFeedSection";
import CommunityFollowButton from "../../Community/CommunityFollowButton";
import { fetchCommunityFollows } from "../../Community/communitySocialApi";
import {
  fetchShopProducts,
  formatShopPrice,
} from "../../Shop/shopApi";
import { projectPublicPath } from "../../Helper/entityRoutes";
import {
  normalizeSocialNetworkKey,
  socialNetworkMeta,
  extractCustomIconUrl,
} from "../../Helper/socialNetworks";
import { shouldRenderEntityStorefrontSection, filterEntityPublicShopProducts } from "../../Organization/Public/entityStorefrontVisibility";
import EntityPublicVideoLibrary from "../../Organization/Public/EntityPublicVideoLibrary";
import "../../Organization/Public/OrganizationPublicPage.css";
import "../../Community/CommunitySocial.css";
import "./ProjectPublicPage.css";

/**
 * Public Project page — canonical /project/:slug
 * Organization public shell + shared EntityIdentity / Brand Links / AE.
 * Product exception — right column: About → Fundraising → Latest Videos → Storefront.
 * Legacy /projects/:slug permanently redirects (Phase D.4.1).
 */
class ProjectPublicPage extends Helper {
  state = {
    loading: true,
    project: null,
    error: false,
    isFollowing: false,
    products: [],
    productsLoading: false,
    aboutExpanded: false,
    videoDetailsFirst: null,
    redirect: false,
    redirectPPV: false,
    redirectPaymentOption: false,
    redirectSubscription: false,
    playButtonClicked: false,
  };

  componentDidMount() {
    this.setState({ playButtonClicked: false });
    this.loadProject();
  }

  componentDidUpdate(prevProps) {
    const prevSlug =
      prevProps.match && prevProps.match.params
        ? prevProps.match.params.slug
        : "";
    const nextSlug =
      this.props.match && this.props.match.params
        ? this.props.match.params.slug
        : "";
    if (prevSlug !== nextSlug) {
      this.setState({ playButtonClicked: false });
      this.loadProject();
    }
  }

  loadProject = async () => {
    const slug =
      this.props.match && this.props.match.params
        ? this.props.match.params.slug
        : "";
    this.setState({
      loading: true,
      error: false,
      isFollowing: false,
      products: [],
    });
    const project = await fetchProjectBySlug(slug);
    let isFollowing = !!(project && project.is_following);
    if (project && project.id && !isFollowing) {
      const hasSession =
        localStorage.getItem("userId") && localStorage.getItem("accessToken");
      if (hasSession) {
        const follows = await fetchCommunityFollows("project");
        if (!follows.error) {
          isFollowing = (follows.follows || []).some(
            (row) => Number(row.followable_id) === Number(project.id)
          );
        }
      }
    }
    this.setState({
      loading: false,
      project,
      error: !project,
      isFollowing,
    });
    if (project) {
      this.loadProducts(project);
    }
  };

  loadProducts = async (project) => {
    this.setState({ productsLoading: true });
    const orgId =
      (project.organization && project.organization.id) ||
      project.organization_id;
    if (!orgId) {
      this.setState({ products: [], productsLoading: false });
      return;
    }
    const result = await fetchShopProducts({
      organization_id: orgId,
      per_page: 24,
    });
    this.setState({
      products: (result && result.products) || [],
      productsLoading: false,
    });
  };

  handlePlayVideo = async (event, adminVideoId) => {
    event.preventDefault();

    const inputData = {
      admin_video_id: adminVideoId,
      skip: 0,
    };

    var details = await this.onlySingleVideoFirst(inputData);

    if (!details) {
      ToastDemo(
        this.props.toastManager,
        (this.state.videoDetailsError &&
          this.state.videoDetailsError.error_messages) ||
          "Unable to start playback",
        "error"
      );
      return;
    }

    this.redirectStatus(details);
    this.setState({ playButtonClicked: true });
  };

  getBrandLinks(project) {
    if (!project) {
      return [];
    }

    const fromPayload =
      project.brand_links && Array.isArray(project.brand_links.links)
        ? project.brand_links.links
        : [];

    const filtered = fromPayload.filter(
      (link) =>
        link &&
        link.url &&
        link.is_enabled !== false &&
        link.is_visible !== false
    );
    if (filtered.length) {
      return filtered;
    }

    const social = project.social_links || {};
    if (!social || typeof social !== "object" || Array.isArray(social)) {
      return [];
    }

    const built = [];
    let order = 10;
    Object.keys(social).forEach((key) => {
      const network = normalizeSocialNetworkKey(key);
      const value = social[key];
      const url = String(
        value && typeof value === "object" ? value.url || "" : value || ""
      ).trim();
      if (!url) {
        return;
      }
      const meta = socialNetworkMeta(network);
      const iconUrl =
        value && typeof value === "object" ? extractCustomIconUrl(value) : null;
      const item = {
        id: "project-social-client-" + (network || "link") + "-" + order,
        display_name:
          (value && value.display_name) || meta.label || network,
        url,
        builtin_icon:
          (value && value.builtin_icon) ||
          meta.builtin_icon ||
          "fas fa-link",
        display_order: order,
        open_in_new_tab: true,
        is_enabled: true,
        is_visible: true,
        css_class: meta.css_class || network,
      };
      if (iconUrl) {
        item.icon_type = "custom";
        item.icon_url = iconUrl;
      }
      built.push(item);
      order += 10;
    });
    return built;
  }

  renderLogo(project) {
    if (project.logo_path) {
      return (
        <img
          src={resolveMediaUrl(project.logo_path)}
          alt={project.name || "Project"}
          className="org-logo"
          onError={handleMediaImgError}
          loading="lazy"
        />
      );
    }

    return (
      <div
        className="org-logo-placeholder"
        aria-hidden="true"
        title={project.name || "Project"}
      >
        <i className="fas fa-seedling" />
      </div>
    );
  }

  renderVideoCarousel(videos, videoSubcategories) {
    return (
      <EntityPublicVideoLibrary
        videos={videos}
        videoSubcategories={videoSubcategories}
        onPlayVideo={this.handlePlayVideo}
        keyPrefix="project"
      />
    );
  }

  renderStorefront(products) {
    const visibleProducts = filterEntityPublicShopProducts(products);
    if (!visibleProducts.length) {
      return null;
    }

    return (
      <div className="org-storefront-grid">
        {visibleProducts.map((product) => {
          const priceLabel = product.is_free
            ? "Free"
            : formatShopPrice(product.effective_price);
          return (
            <Link
              key={product.id}
              className="org-storefront-card"
              to={`/shop/product/${encodeURIComponent(product.slug)}`}
            >
              <div className="org-storefront-card__media">
                {product.primary_image ? (
                  <img
                    src={product.primary_image}
                    alt={product.name || ""}
                    loading="lazy"
                    onError={handleMediaImgError}
                  />
                ) : (
                  <div className="org-storefront-card__placeholder" />
                )}
              </div>
              <div className="org-storefront-card__body">
                <strong>{product.name}</strong>
                {priceLabel ? <span>{priceLabel}</span> : null}
              </div>
            </Link>
          );
        })}
      </div>
    );
  }

  renderAbout(project) {
    const html = project.description || project.summary || "";
    const plain = html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
    const PREVIEW_LEN = 280;
    const needsCollapse = plain.length > PREVIEW_LEN;
    const expanded = this.state.aboutExpanded;

    let previewText = plain;
    if (needsCollapse && !expanded) {
      let cut = plain.slice(0, PREVIEW_LEN);
      const lastSpace = cut.lastIndexOf(" ");
      if (lastSpace > Math.floor(PREVIEW_LEN * 0.55)) {
        cut = cut.slice(0, lastSpace);
      }
      previewText = cut.trimEnd() + "…";
    }

    return (
      <section
        className="org-about-section"
        aria-labelledby="project-about-heading"
      >
        <h2 id="project-about-heading" className="org-videos-heading">
          About
        </h2>
        <div className="org-about-body">
          {needsCollapse && !expanded ? (
            <p className="org-description">{previewText}</p>
          ) : html ? (
            <RichTextContent html={html} className="org-description rich-text" />
          ) : (
            <p className="org-empty-inline">
              Welcome to {project.name || "this project"}.
            </p>
          )}
        </div>
        {needsCollapse ? (
          <AppearanceButton
            variant="readmore"
            type="button"
            className="org-about-toggle"
            onClick={() =>
              this.setState({ aboutExpanded: !this.state.aboutExpanded })
            }
          >
            {expanded ? "Read Less" : "Read More"}
          </AppearanceButton>
        ) : null}
      </section>
    );
  }

  render() {
    const {
      loading,
      project,
      error,
      playButtonClicked,
      products,
      productsLoading,
    } = this.state;

    if (playButtonClicked) {
      const returnToVideo = this.renderRedirectPage(
        this.state.videoDetailsFirst
      );
      if (returnToVideo != null) {
        return returnToVideo;
      }
    }

    if (loading) {
      return (
        <div className="main-sec-content" style={{ padding: "3rem 1rem" }}>
          <EmptyState
            title="Loading…"
            message="Loading project profile."
            statusRole="status"
          />
        </div>
      );
    }

    if (error || !project) {
      return (
        <div className="main-sec-content" style={{ padding: "3rem 1rem" }}>
          <EmptyState
            title="Project not found"
            message="We couldn't find that project. It may have been removed or the link is incorrect."
          >
            <Link to="/community/projects" className="ae-btn ae-btn--primary">
              Browse Projects
            </Link>
          </EmptyState>
        </div>
      );
    }

    const experience =
      project.experience || { type: "none", should_present: false };
    const funding = project.funding || {};
    const hasCover = Boolean(project.cover_image_path);
    const brandLinks = this.getBrandLinks(project);
    const coverStyle = hasCover
      ? {
          backgroundImage: resolveMediaBackground(project.cover_image_path),
        }
      : undefined;
    const typeLabel =
      project.type_label ||
      project.project_type_label ||
      (project.project_type === "ongoing" ? "Ongoing Project" : "Project");
    const donatePath = `/project/${project.slug || project.id}/donate`;
    const canonicalPath =
      project.public_path || projectPublicPath(project.slug || project.id);
    const canonicalHref =
      (typeof window !== "undefined" ? window.location.origin : "") +
      canonicalPath;
    const externalMode =
      project.donation_mode === "external" ||
      (!project.donation_mode &&
        !!(funding.donation_url || project.donation_url));

    return (
      <ExperienceGate destinationType="project" presentation={experience}>
        <Helmet>
          <title>{project.name || "Project"}</title>
          <link rel="canonical" href={canonicalHref} />
        </Helmet>
        <div className="organization-public-page project-public-page">
          {hasCover ? (
            <div
              className="org-hero org-hero--cover"
              style={coverStyle}
              role="img"
              aria-label={(project.name || "Project") + " cover"}
            />
          ) : (
            <div className="org-hero org-hero--plain" />
          )}

          <div className="org-page-shell">
            <header className="org-header org-header--identity">
              <div className="org-header-inner">
                {this.renderLogo(project)}
                <EntityIdentity
                  name={project.name}
                  handle={project.slug}
                  typeLabel={typeLabel}
                  entity={project}
                />
              </div>

              <div className="org-actions-block">
                <div
                  className="org-action-bar org-action-bar--primary"
                  role="group"
                  aria-label="Project actions"
                >
                  <CommunityFollowButton
                    type="project"
                    id={project.id}
                    initiallyFollowing={
                      this.state.isFollowing || !!project.is_following
                    }
                    followerCount={
                      typeof project.follower_count === "number"
                        ? project.follower_count
                        : null
                    }
                    showCount
                    className="ae-btn ae-btn--secondary ae-btn--entity-follow org-action-btn org-follow-btn"
                    onChange={(detail) => {
                      this.setState({
                        isFollowing: !!(detail && detail.following),
                        project: {
                          ...project,
                          is_following: !!(detail && detail.following),
                          follower_count:
                            detail && typeof detail.follower_count === "number"
                              ? detail.follower_count
                              : project.follower_count,
                        },
                      });
                    }}
                  />
                  {externalMode &&
                  (funding.donation_url || project.donation_url) ? (
                    <AppearanceButton
                      href={funding.donation_url || project.donation_url}
                      variant="donate"
                      className="org-action-btn"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Donate
                    </AppearanceButton>
                  ) : (
                    <AppearanceButton
                      to={donatePath}
                      variant="donate"
                      className="org-action-btn"
                    >
                      Donate
                    </AppearanceButton>
                  )}
                  <OrganizationActionBar
                    organizationName={project.name}
                    website={project.website}
                    donationUrl={null}
                    extraActions={[]}
                  />
                  <ShareMenu
                    title={project.name}
                    text={`Check out ${project.name}`}
                    className="org-action-btn"
                    variant="share"
                  />
                </div>
                {brandLinks.length ? (
                  <BrandLinksRow
                    links={brandLinks}
                    className="social-share org-brand-links"
                  />
                ) : null}
              </div>
            </header>

            <div className="org-content-layout org-content-layout--home">
              <div className="org-content-main">
                <ActivityFeedSection
                  contextType="project"
                  projectId={project.id}
                  title="Activity"
                  headingId="project-activity-heading"
                  className="org-activity-feed"
                />
                <ProjectUpdates updates={project.updates} />
              </div>

              <aside
                className="org-content-sidebar"
                aria-label="Project about, progress, fundraising, media, and storefront"
              >
                {this.renderAbout(project)}

                <ProjectMilestones
                  milestones={project.milestones}
                  activeMilestone={project.active_milestone}
                  milestoneHistory={project.milestone_history}
                  projectType={project.project_type || "one_time"}
                  progressPercent={project.progress_percent}
                  supportPath={null}
                  donationUrl={null}
                />

                <ProjectFundingPanel
                  funding={{
                    ...funding,
                    support_path: null,
                    donation_url: null,
                  }}
                  projectName={project.name}
                />

                <section
                  className="org-videos-section"
                  aria-labelledby="project-videos-heading"
                >
                  <h2 id="project-videos-heading" className="org-videos-heading">
                    Latest Videos
                  </h2>
                  {this.renderVideoCarousel(
                    project.videos,
                    project.video_subcategories
                  )}
                </section>

                {shouldRenderEntityStorefrontSection({
                  products,
                  productsLoading,
                }) ? (
                  <section
                    className="org-storefront-section"
                    aria-labelledby="project-storefront-heading"
                  >
                    <h2
                      id="project-storefront-heading"
                      className="org-videos-heading"
                    >
                      Storefront
                    </h2>
                    {this.renderStorefront(products)}
                  </section>
                ) : null}
              </aside>
            </div>
          </div>
        </div>
      </ExperienceGate>
    );
  }
}

export default withToastManager(ProjectPublicPage);
